import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useAuditQuery, useAuditExport } from '../../api/hooks/audit';
import { AuditSearchFilters } from '../../api/services/auditService';
import { LoadingSpinner, ErrorMessage } from '../common';
import { format } from 'date-fns';
import { Filter, Download, User, Clock, Server, CheckCircle, XCircle, Search } from 'lucide-react';
import {
  Button,
  Badge,
  Card,
  PageHeader,
  Input,
} from '@license-management/ui';

export const AuditLogViewer: React.FC = () => {
  const [filters, setFilters] = useState<AuditSearchFilters>({
    limit: 20,
    offset: 0
  });
  
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState('');
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data, isLoading, error, isFetching } = useAuditQuery({
    ...filters,
    offset: (page - 1) * (filters.limit || 20)
  });
  
  const exportMutation = useAuditExport();

  const totalPages = useMemo(() => {
    if (!data) return 0;
    return Math.ceil(data.total / (filters.limit || 20));
  }, [data, filters.limit]);

  // Debounced search handler
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: searchValue || undefined }));
      setPage(1);
    }, 300); // 300ms debounce

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchValue]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchValue(value);
  }, []);

  const handleExport = () => {
    exportMutation.mutate(filters);
  };

  const getEventTypeVariant = (eventType: string): 'success' | 'info' | 'error' | 'default' | 'primary' | 'warning' => {
    const variants: Record<string, 'success' | 'info' | 'error' | 'default' | 'primary' | 'warning'> = {
      create: 'success',
      update: 'info',
      delete: 'error',
      read: 'default',
      login: 'primary',
      logout: 'warning'
    };
    return variants[eventType] || 'default';
  };

  const getResourceIcon = (resourceType: string) => {
    const icons: Record<string, string> = {
      license: '🎫',
      customer: '👤',
      user: '👥',
      system: '⚙️',
      auth: '🔐',
      analytics: '📊'
    };
    return icons[resourceType] || '📄';
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Audit Log"
        description="Track all system activity and changes"
        actions={
          <div className="flex items-center gap-3">
            {isFetching && (
              <div className="text-sm text-zinc-500 flex items-center gap-1">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                <span>Updating...</span>
              </div>
            )}
            <Button
              variant="secondary"
              onClick={() => setShowFilters(!showFilters)}
              leftIcon={<Filter className="h-4 w-4" />}
            >
              Filters
            </Button>
            <Button
              onClick={handleExport}
              disabled={exportMutation.isPending}
              variant="secondary"
              leftIcon={<Download className="h-4 w-4" />}
            >
              {exportMutation.isPending ? 'Exporting...' : 'Export CSV'}
            </Button>
          </div>
        }
      />

      {/* Search Bar */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <Input
            placeholder="Search by user email, resource ID, or action..."
            value={searchValue}
            onChange={(e) => handleSearchChange(e.target.value)}
            leftElement={<Search className="h-4 w-4" />}
          />
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <Card className="p-4">
          <h3 className="font-medium text-zinc-900 mb-4">Filter Options</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Resource Type Filter */}
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1">
                Resource Type
              </label>
              <select
                className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                value={filters.resourceType || ''}
                onChange={(e) => {
                  setFilters({ ...filters, resourceType: e.target.value || undefined });
                  setPage(1);
                }}
              >
                <option value="">All Resources</option>
                <option value="license">Licenses</option>
                <option value="customer">Customers</option>
                <option value="user">Users</option>
                <option value="system">System</option>
                <option value="auth">Authentication</option>
                <option value="analytics">Analytics</option>
              </select>
            </div>

            {/* Event Type Filter */}
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1">
                Event Type
              </label>
              <select
                className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                value={filters.eventType || ''}
                onChange={(e) => {
                  setFilters({ ...filters, eventType: e.target.value || undefined });
                  setPage(1);
                }}
              >
                <option value="">All Events</option>
                <option value="create">Create</option>
                <option value="update">Update</option>
                <option value="delete">Delete</option>
                <option value="read">Read</option>
                <option value="login">Login</option>
                <option value="logout">Logout</option>
              </select>
            </div>

            {/* Date Range */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">
                  Start Date
                </label>
                <input
                  type="datetime-local"
                  className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  value={filters.startDate || ''}
                  onChange={(e) => {
                    setFilters({ ...filters, startDate: e.target.value || undefined });
                    setPage(1);
                  }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">
                  End Date
                </label>
                <input
                  type="datetime-local"
                  className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  value={filters.endDate || ''}
                  onChange={(e) => {
                    setFilters({ ...filters, endDate: e.target.value || undefined });
                    setPage(1);
                  }}
                />
              </div>
            </div>
          </div>

          {/* Clear Filters */}
          <div className="mt-4 flex justify-end">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setFilters({ limit: 20, offset: 0 });
                setSearchValue('');
                setPage(1);
              }}
            >
              Clear Filters
            </Button>
          </div>
        </Card>
      )}

      {/* Audit Log Table */}
      <Card className="overflow-hidden">
        {/* Show error state */}
        {error && (
          <div className="p-8">
            <ErrorMessage message="Failed to load audit logs" />
          </div>
        )}

        {/* Show loading state only on initial load */}
        {isLoading && !data && (
          <div className="p-8 flex justify-center">
            <LoadingSpinner />
          </div>
        )}

        {/* Show table when we have data or are fetching new data */}
        {(data || !isLoading) && !error && (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-zinc-200">
                <thead className="bg-zinc-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                      Timestamp
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                      Action
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                      Resource
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                      Details
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-zinc-200">
                  {data?.entries.map((entry) => (
                    <tr key={entry.audit_id} className="hover:bg-zinc-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-900">
                        <div className="flex items-center gap-1">
                          <Clock className="h-4 w-4 text-zinc-400" />
                          {format(new Date(entry.timestamp), 'MMM d, yyyy HH:mm:ss')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex items-center gap-1">
                          <User className="h-4 w-4 text-zinc-400" />
                          <div>
                            <div className="text-zinc-900">{entry.user_email}</div>
                            <div className="text-zinc-500 text-xs">{entry.user_role}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge variant={getEventTypeVariant(entry.event_type)}>
                          {entry.event_type.toUpperCase()}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{getResourceIcon(entry.resource_type)}</span>
                          <div>
                            <div className="text-zinc-900">{entry.resource_type}</div>
                            {entry.resource_id && (
                              <div className="text-zinc-500 text-xs font-mono">{entry.resource_id}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-zinc-500">
                        <div className="max-w-xs truncate" title={entry.changes_summary}>
                          {entry.changes_summary || entry.api_endpoint || '-'}
                        </div>
                        {entry.ip_address && (
                          <div className="text-xs text-zinc-400 flex items-center gap-1 mt-1">
                            <Server className="h-3 w-3" />
                            {entry.ip_address}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {entry.success ? (
                          <CheckCircle className="h-5 w-5 text-green-500" />
                        ) : (
                          <div className="flex items-center gap-1">
                            <XCircle className="h-5 w-5 text-red-500" />
                            {entry.error_message && (
                              <span className="text-xs text-red-600" title={entry.error_message}>
                                Error
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Empty State */}
            {data?.entries.length === 0 && (
              <div className="text-center py-12">
                <p className="text-zinc-500">No audit logs found</p>
              </div>
            )}

            {/* Pagination */}
            <div className="bg-zinc-50 px-6 py-3 flex items-center justify-between border-t border-zinc-200">
              <div className="flex items-center gap-4">
                <div className="text-sm text-zinc-700">
                  Showing {((page - 1) * (filters.limit || 20)) + 1} to{' '}
                  {Math.min(page * (filters.limit || 20), data?.total || 0)} of{' '}
                  {data?.total || 0} results
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-zinc-600">Per page:</label>
                  <select
                    className="text-sm border border-zinc-300 rounded-lg px-2 py-1"
                    value={filters.limit || 20}
                    onChange={(e) => {
                      setFilters({ ...filters, limit: parseInt(e.target.value) });
                      setPage(1);
                    }}
                  >
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                </div>
              </div>
              {totalPages > 1 && (
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                  >
                    Previous
                  </Button>
                  <span className="px-3 py-1 text-sm text-zinc-700">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setPage(page + 1)}
                    disabled={page === totalPages}
                  >
                    Next
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </Card>
    </div>
  );
};